import {
	EActionState,
	IScheduledAction,
	IScheduledActionGroup
} from '@/models';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialState } from './state';
import { scheduleService } from '@/services/schedule.service';

export const scheduleSlice = createSlice({
	name: '@@schedule',
	initialState: initialState,
	reducers: {
		INIT_START: state => {
			state.init = EActionState.RUNNING;
		},
		INIT_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			state.actions.data = action.payload;
			state.init = EActionState.COMPLETED;
		},
		INIT_FAILED: state => {
			state.actions.data = initialState.actions.data;
			state.init = EActionState.FAILED;
		},
		SET_PAGE_START: (state, action: PayloadAction<number>) => {
			state.actions.page = action.payload;
			state.actions.loaded = EActionState.RUNNING;
			state.previousState = state.actions;
		},
		SET_PAGE_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			scheduleService.reconcileActions(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
			state.actions.data = action.payload;
			state.actions.loaded = EActionState.COMPLETED;
			state.previousState = state.actions;
		},
		SET_PAGE_FAILED: state => {
			state.actions = state.previousState;
			state.actions.loaded = EActionState.FAILED;
		},
		SET_QUERY_START: (state, action: PayloadAction<string>) => {
			state.actions.query = action.payload;
			state.actions.page = 1;
			state.actions.loaded = EActionState.RUNNING;
			state.previousState = state.actions;
		},
		SET_QUERY_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			scheduleService.reconcileActions(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
			state.actions.data = action.payload;
			state.actions.loaded = EActionState.COMPLETED;
			state.previousState = state.actions;
		},
		SET_QUERY_FAILED: state => {
			state.actions = state.previousState;
			state.actions.loaded = EActionState.FAILED;
		},
		SET_SELECTED: (
			state,
			action: PayloadAction<{
				action: IScheduledAction;
				checked: boolean;
			}>
		) => {
			scheduleService.setSelected(
				state.selectedActions,
				state.tracker.actions,
				action.payload.action,
				action.payload.checked
			);
		},
		TRIGGER_START: state => {
			state.triggerState = EActionState.RUNNING;
		},
		TRIGGER_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			state.actions.data = action.payload;
			state.selectedActions = initialState.selectedActions;
			scheduleService.reconcileActions(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
			state.triggerState = EActionState.COMPLETED;
		},
		TRIGGER_FAILED: state => {
			state.triggerState = EActionState.FAILED;
		},
		SET_TRACKER_OPEN: state => {
			state.tracker.open = !state.tracker.open;
		},
		DESELECT_ALL: state => {
			state.selectedActions.forEach(a => {
				const index = state.tracker.actions.findIndex(
					t => t.data.key === a.key
				);
				if (index !== -1) {
					state.tracker.actions.splice(index, 1);
				}
			});
			state.selectedActions = initialState.selectedActions;
		},
		CLEAR_TRACKED: (state, action: PayloadAction<string | undefined>) => {
			scheduleService.clearTracked(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
		},
		REFRESH_ACTION_START: (
			state,
			action: PayloadAction<IScheduledAction>
		) => {
			state.tracker.selected = action.payload;
			scheduleService.onRefreshActionStart(
				state.tracker.actions,
				action.payload.key
			);
		},
		REFRESH_ACTION_COMPLETED: (
			state,
			action: PayloadAction<IScheduledAction>
		) => {
			scheduleService.onRefreshActionCompleted(
				state.actions.data,
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
		},
		REFRESH_ACTION_FAILED: state => {
			scheduleService.onRefreshActionFailed(
				state.tracker.actions,
				state.tracker.selected?.key ?? ''
			);
			state.tracker.selected = initialState.tracker.selected;
		},
		REFRESH_ACTIONS_START: state => {
			state.actions.loaded = EActionState.RUNNING;
		},
		REFRESH_ACTIONS_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			scheduleService.reconcileActions(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
			state.actions.data = action.payload;
			state.actions.loaded = EActionState.COMPLETED;
		},
		REFRESH_ACTIONS_FAILED: state => {
			state.actions.loaded = EActionState.FAILED;
		},
		TRIGGER_TRACKED_START: (
			state,
			action: PayloadAction<IScheduledAction>
		) => {
			state.tracker.selected = action.payload;
			state.triggerState = EActionState.RUNNING;
		},
		TRIGGER_TRACKED_COMPLETED: (
			state,
			action: PayloadAction<IScheduledActionGroup[]>
		) => {
			state.actions.data = action.payload;
			scheduleService.reconcileActions(
				state.selectedActions,
				state.tracker.actions,
				action.payload
			);
			state.triggerState = EActionState.COMPLETED;
			state.tracker.selected = initialState.tracker.selected;
		},
		TRIGGER_TRACKED_FAILED: state => {
			state.triggerState = EActionState.FAILED;
			state.tracker.selected = initialState.tracker.selected;
		}
	}
});

export const actionCreator = scheduleSlice.actions;
export const reducer = scheduleSlice.reducer;
